import { useNavigate } from "react-router";
import { useAuth } from "react-oidc-context";
import { ExternalLink, Loader2, LogOut, X } from "lucide-react";
import { Trans } from "@lingui/react/macro";
import { useMyStudioSections } from "../hooks/useMyStudioSections.ts";
import { ActionSidebarItem } from "./Sidebar.tsx";

interface StudioSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function StudioSheet({ isOpen, onClose }: StudioSheetProps) {
  const sections = useMyStudioSections();
  const navigate = useNavigate();
  const auth = useAuth();

  if (!isOpen) return null;

  function handleItemClick(path: string) {
    navigate(`/mystudio/${path}`);
    onClose();
  }

  function handleActionItemClick(item: ActionSidebarItem) {
    item.onAction();
    onClose();
  }

  function handleLogout() {
    onClose();
    auth.signoutRedirect().catch(console.error);
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/25 sm:hidden"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        className="border-soluud-border fixed right-0 bottom-16 left-0 z-50 mx-2 mb-1 animate-[sheet-enter_280ms_cubic-bezier(0.32,0.72,0,1)] overflow-hidden rounded-2xl border bg-white shadow-[--shadow-elevated] sm:hidden"
        role="dialog"
        aria-modal="true"
        aria-label="My Studio navigation"
      >
        {/* Header */}
        <div className="border-soluud-border-subtle flex items-center justify-between border-b px-4 py-3.5">
          <h2 className="text-soluud-text-primary text-sm font-semibold">
            <Trans>My Studio</Trans>
          </h2>
          <button
            onClick={onClose}
            className="text-soluud-text-tertiary hover:text-soluud-text-primary focus-visible:ring-soluud-primary flex h-8 w-8 items-center justify-center rounded-lg transition-colors focus-visible:ring-2 focus-visible:outline-none"
            aria-label="Close"
          >
            <X className="h-4.5 w-4.5" />
          </button>
        </div>

        {/* Sections */}
        <div className="px-2 py-2">
          {sections.map((section, sectionIndex) => (
            <div key={section.label ?? sectionIndex}>
              {sectionIndex > 0 && (
                <div className="border-soluud-border-subtle mx-2 my-1.5 border-t" />
              )}
              {section.label && (
                <p className="text-soluud-text-tertiary mt-1 mb-0.5 px-3 text-[11px] font-semibold tracking-wider uppercase">
                  {section.label}
                </p>
              )}
              <ul>
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.key}>
                      <button
                        onClick={() => handleItemClick(item.path)}
                        className="text-soluud-text-primary hover:bg-soluud-background flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm transition-colors"
                      >
                        <Icon className="text-soluud-text-secondary h-5 w-5 shrink-0" />
                        <span>{item.label}</span>
                      </button>
                    </li>
                  );
                })}
                {section.actionItems?.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.key}>
                      <button
                        onClick={() => handleActionItemClick(item)}
                        disabled={item.isLoading}
                        className="text-soluud-text-primary hover:bg-soluud-background flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {item.isLoading ? (
                          <Loader2 className="text-soluud-text-secondary h-5 w-5 shrink-0 animate-spin" />
                        ) : (
                          <Icon className="text-soluud-text-secondary h-5 w-5 shrink-0" />
                        )}
                        <span className="flex-1 text-left">{item.label}</span>
                        {item.isExternal && !item.isLoading && (
                          <ExternalLink className="text-soluud-text-tertiary h-4 w-4 shrink-0" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Footer — logout */}
        <div className="border-soluud-border-subtle border-t px-2 py-2">
          <button
            onClick={handleLogout}
            className="text-soluud-text-secondary flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm transition-colors hover:bg-red-50 hover:text-red-600"
          >
            <LogOut className="h-5 w-5 shrink-0" />
            <span>
              <Trans>Sign out</Trans>
            </span>
          </button>
        </div>
      </div>
    </>
  );
}
